import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Trash2, Package } from "lucide-react";

const STATUSES = ["pending", "reviewed", "approved", "rejected", "delivered"];

export default function AdminFamily() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const load = useCallback(async () => {
    const params = status ? `?status=${status}` : "";
    const r = await api.get(`/admin/family-support${params}`);
    setItems(r.data);
  }, [status]);
  useEffect(() => { document.title = "Admin · Family Food Support"; load(); }, [load]);

  const update = async (id, patch) => {
    try { await api.put(`/admin/family-support/${id}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/family-support/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-family">
      <h1 className="font-serif text-3xl font-bold mb-2">Family Food Support</h1>
      <p className="text-[#0a0d12]/60 text-sm mb-6">Two families approved per quarter based on needs assessment.</p>

      <div className="mb-4 flex gap-2 flex-wrap">
        <button onClick={() => setStatus("")} className={`px-3 py-1.5 rounded text-sm font-semibold ${status === "" ? "bg-[#155e3f] text-[#d6aa43]" : "bg-white border border-black/10"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded text-sm font-semibold capitalize ${status === s ? "bg-[#155e3f] text-[#d6aa43]" : "bg-white border border-black/10"}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="bg-white rounded-xl border border-black/5 p-5" data-testid={`family-${a.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
              <div>
                <div className="font-mono text-xs text-[#0d1724]">{a.serial}</div>
                <div className="font-serif text-lg text-[#0a0d12] font-bold">{a.household_head_name}</div>
                <div className="text-xs text-[#0a0d12]/60">{a.email} · {a.phone} · {a.state}/{a.lga} · Quarter: {a.quarter}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.2em] text-[#0d1724]/60">Score</div>
                <div className="font-serif text-3xl font-bold text-[#d6aa43]">{a.total_score}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs mb-3">
              {Object.entries(a.scores || {}).map(([k, v]) => (
                <div key={k} className="p-2 rounded bg-[#f8f4e8]"><div className="text-[10px] text-[#0d1724]/60 uppercase">{k.replace(/_/g, " ")}</div><div className="font-bold">{v}</div></div>
              ))}
            </div>
            <p className="text-sm text-[#0a0d12]/80 mb-3 line-clamp-3">{a.reason}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${a.status === "approved" || a.status === "delivered" ? "bg-green-100 text-green-800" : a.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-800"}`}>{a.status}</span>
              <div className="flex-1"></div>
              <select value={a.status} onChange={(e) => update(a.id, { status: e.target.value })} className="text-xs border border-black/15 rounded px-2 py-1">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => remove(a.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#0a0d12]/50">No applications.</p>}
      </div>
    </div>
  );
}
